import React, { useRef, useEffect, CSSProperties, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { TimelineProps, TimelineItem } from "./types";
import TimelineCard from "../cards/timelineCard";
import TimelineImage from "./timelineImage";
import TimelineVerticalLine from "./timelineVerticalLine";
import DashedLine from "./dashedLine";
import LoadingTimeline from "./loadingTimeline";
import TimelineLegend from "./timelineLegend";
import { PiCaretCircleDoubleUp } from "react-icons/pi";
import ScrollButton from "./scrollButton";
import { useScrollToTop } from "./hooks";
import { calcDateDuration } from "../../utils";

const ESTIMATE_ITEM_HEIGHT = 225;

function smoothScrollTo(
    element: HTMLElement,
    targetOffset: number,
    durationMs: number
) {
    const startOffset = element.scrollTop;
    const distance = targetOffset - startOffset;
    let startTime: number | null = null;

    function step(timestamp: number) {
        if (startTime === null) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const t = Math.min(elapsed / durationMs, 1);
        const easedT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        element.scrollTop = startOffset + distance * easedT;

        if (t < 1) {
            window.requestAnimationFrame(step);
        }
    }

    window.requestAnimationFrame(step);
}

const VirtualizedTimeline: React.FC<TimelineProps> = ({
    items,
    title,
    isLoading,
    selectedEventId,
    onEventSelect,
    onEventHover,
    cardShouldFlash,
    onEndFlash,
    selectedCamera,
    disableVirtualization = false,
}) => {
    const parentRef = useRef<HTMLDivElement>(null);
    const { scrollRef, showScrollToTop, scrollToTop } = useScrollToTop();
    const lastItemsLengthRef = useRef(items.length);
    const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

    const setRefs = (el: HTMLDivElement | null) => {
        (parentRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    };

    const rowVirtualizer = useVirtualizer({
        count: disableVirtualization ? 0 : items.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => ESTIMATE_ITEM_HEIGHT,
        overscan: 5,
    });

    const handleCardHover = (hovered: boolean, publicId: string) => {
        setHoveredCardId(hovered ? publicId : null);
        onEventHover?.(hovered ? publicId : null);
    };

    const isConsecutive = (currentIndex: number, nextIndex: number) => {
        return currentIndex === nextIndex - 1;
    };

    // Handles scrolling when there is a big change in list size (like deleting a filter to show all)
    useEffect(() => {
        if (disableVirtualization || !selectedEventId || !parentRef.current) return;

        const itemsLengthChanged = Math.abs(lastItemsLengthRef.current - items.length) > 5;
        lastItemsLengthRef.current = items.length;

        // Only directly scroll to selection when items array changes significantly
        if (!itemsLengthChanged) return;

        const index = items.findIndex((it) => it.publicId === selectedEventId);
        if (index === -1) return;

        // Jump to selected event to prevent unnecessary requests
        if (parentRef.current) {
            const targetOffset = index * ESTIMATE_ITEM_HEIGHT;
            parentRef.current.scrollTop = targetOffset - 50;

            rowVirtualizer.measure();
        }
    }, [selectedEventId, items, disableVirtualization, rowVirtualizer]);

    // Handles normal selection scrolling (not filter transitions)
    useEffect(() => {
        if (disableVirtualization || !selectedEventId || !parentRef.current) return;

        if (Math.abs(lastItemsLengthRef.current - items.length) > 5) return;

        const index = items.findIndex((it) => it.publicId === selectedEventId);
        if (index === -1) return;

        const virtualItems = rowVirtualizer.getVirtualItems();
        const targetItem = virtualItems.find((v) => v.index === index);

        const targetOffset =
            targetItem != null ? targetItem.start : index * ESTIMATE_ITEM_HEIGHT;

        smoothScrollTo(parentRef.current, targetOffset - 50, 800);
    }, [selectedEventId, rowVirtualizer, disableVirtualization]);

    function renderRow(
        item: TimelineItem,
        idx: number,
        translateY?: number | null
    ) {
        if (!item) return null;

        const nextItem = items[idx + 1];
        const showLine =
            idx < items.length - 1 &&
            nextItem &&
            isConsecutive(item.index, nextItem.index);

        const lineElement = idx !== items.length - 1 ? (
            showLine ? (
                <TimelineVerticalLine />
            ) : (
                <DashedLine timeDiff={calcDateDuration(item.date, nextItem?.date)} />
            )
        ) : null;

        const isLeft = idx % 2 === 1;
        const cardWrapperClass = isLeft ? "left-1/2 pl-8" : "right-1/2 pr-8";
        const imageWrapperClass = isLeft ? "right-1/2 pr-8" : "left-1/2 pl-8";

        const handleCardClick = () => {
            if (item.lat && item.lng) {
                onEventSelect?.(item.publicId, item.lng, item.lat, {
                    eventType: item.title,
                    eventTimestamp: item.date,
                    freeText: item.description,
                    depth: item.depth ?? undefined,
                    eventOptions: item.rawEvent?.event_options,
                    timeFromStart: item.timeFromStart,
                });
            } else {
                onEventSelect?.(item.publicId);
            }
        };

        const onHoverCard = (hovered: boolean) => handleCardHover(hovered, item.publicId);

        const rowStyle: CSSProperties =
            !disableVirtualization && translateY != null
                ? {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${translateY}px)`,
                }
                : {};

        return (
            <div
                key={item.publicId}
                role="listitem"
                className="relative flex items-center w-full text-blue-500 min-h-[84px]"
                style={rowStyle}
            >
                {lineElement}

                <div className="flex items-center w-full relative">
                    <div className={`absolute ${cardWrapperClass} w-1/2`}>
                        <TimelineCard
                            item={item}
                            className={isLeft ? "mr-auto" : "ml-auto"}
                            isSelected={selectedEventId === item.publicId}
                            onClick={handleCardClick}
                            onHoverChange={onHoverCard}
                            shouldFlash={cardShouldFlash && selectedEventId === item.publicId}
                            onEndFlash={onEndFlash}
                        />
                    </div>
                    <div className={`absolute ${imageWrapperClass} w-1/2`}>
                        <TimelineImage
                            dataAssets={item.data_assets ?? []}
                            selectedCamera={selectedCamera ?? undefined}
                            onHover={onHoverCard}
                        />
                    </div>

                    <div className="absolute left-1/2 transform -translate-x-1/2 z-10">
                        {item.icon ? (
                            <div
                                className={`flex items-center justify-center w-[32px] h-[32px] rounded-full border-2 border-blue-400
                  ${selectedEventId === item.publicId || hoveredCardId === item.publicId
                                        ? "bg-blue-500"
                                        : "bg-white"
                                    }
                  transition-colors duration-200 ease-in-out
                `}
                            >
                                {React.cloneElement(item.icon as React.ReactElement, {
                                    className:
                                        selectedEventId === item.publicId || hoveredCardId === item.publicId
                                            ? "text-white"
                                            : "text-blue-500",
                                })}
                            </div>
                        ) : (
                            <div
                                role="presentation"
                                className={`w-[26px] h-[26px] rounded-full border-4 border-white
                  ${selectedEventId === item.publicId || hoveredCardId === item.publicId
                                        ? "bg-blue-500"
                                        : "bg-blue-200"
                                    }
                  transition-colors duration-200 ease-in-out`}
                            ></div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Render items based on virtualization setting
    let renderedRows;
    if (disableVirtualization) {
        renderedRows = items.map((item, i) => renderRow(item, i));
    } else {
        const virtualItems = rowVirtualizer.getVirtualItems();
        renderedRows = virtualItems.map((virtualItem) => {
            const item = items[virtualItem.index];
            return renderRow(item, virtualItem.index, virtualItem.start);
        });
    }

    return (
        <div className="container mx-auto">
            {title && (
                <div className="text-center">
                    <h2 id="timeline-title" className="text-3xl font-bold text-blue-500 mb-2">
                        {title}
                    </h2>
                </div>
            )}

            <div className="relative">
                <div
                    ref={setRefs}
                    className="relative min-h-[350px] max-h-[450px] 2xl:max-h-[650px] overflow-y-auto mt-1"
                    role="region"
                    aria-labelledby="timeline-title"
                >
                    <TimelineLegend />

                    <div
                        style={
                            !disableVirtualization
                                ? {
                                    height: rowVirtualizer.getTotalSize(),
                                    position: "relative",
                                    width: "100%",
                                }
                                : {}
                        }
                        className="px-1 mt-[145px]"
                    >
                        {renderedRows}
                    </div>

                    {isLoading && <LoadingTimeline className="py-4" />}
                </div>

                {showScrollToTop && (
                    <div className="absolute top-0 right-4">
                        <ScrollButton
                            onClick={scrollToTop}
                            icon={<PiCaretCircleDoubleUp size={38} />}
                            tooltip="Scroll to Top"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default VirtualizedTimeline;